import type { LayoutId } from './input';

/** Egy játékos bemeneti eszköze. */
export type DeviceKind = 'keyboard' | 'gamepad';

export interface PlayerDevice {
  kind: DeviceKind;
  /** billentyűzet-kiosztás */
  layout: LayoutId;
  /** gamepad index (0..3) */
  pad: number;
}

const STORAGE_KEY = 'csf.controls.v2';
const LEGACY_KEY = 'csf.layouts.v1';

export const DEFAULT_DEVICES: PlayerDevice[] = [
  { kind: 'keyboard', layout: 'wasd', pad: 0 },
  { kind: 'keyboard', layout: 'arrows', pad: 1 },
  { kind: 'keyboard', layout: 'ijkl', pad: 2 },
  { kind: 'keyboard', layout: 'numpad', pad: 3 },
];

const isLayout = (value: unknown): value is LayoutId =>
  value === 'wasd' || value === 'arrows' || value === 'ijkl' || value === 'numpad';

const cloneDefaults = (): PlayerDevice[] => DEFAULT_DEVICES.map((d) => ({ ...d }));

const read = (): PlayerDevice[] => {
  const out = cloneDefaults();
  if (typeof window === 'undefined') return out;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (let i = 0; i < out.length; i++) {
          const item = parsed[i] as Partial<PlayerDevice> | undefined;
          if (!item || typeof item !== 'object') continue;
          if (item.kind === 'keyboard' || item.kind === 'gamepad') out[i].kind = item.kind;
          if (isLayout(item.layout)) out[i].layout = item.layout;
          if (typeof item.pad === 'number' && item.pad >= 0 && item.pad <= 3) out[i].pad = item.pad;
        }
        return out;
      }
    }
    // migráció a korábbi, csak billentyűzetes beállításról
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const parsed: unknown = JSON.parse(legacy);
      if (Array.isArray(parsed)) {
        for (let i = 0; i < out.length; i++) if (isLayout(parsed[i])) out[i].layout = parsed[i] as LayoutId;
      }
    }
  } catch {
    /* privát mód / hibás JSON: maradnak a defaultok */
  }
  return out;
};

/** A 4 játékos aktuális eszköze (a UI ezt módosítja, az InputManager ezt olvassa). */
export const devices: PlayerDevice[] = read();

export function saveDevices(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(devices));
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* csendben elmarad */
  }
}

export function setDevice(index: number, patch: Partial<PlayerDevice>): void {
  if (index < 0 || index >= devices.length) return;
  devices[index] = { ...devices[index], ...patch };
  saveDevices();
}

/** Emberi leírás a HUD-hoz/tipphez. */
export function deviceLabel(index: number): string {
  const d = devices[index];
  return d.kind === 'gamepad' ? `Gamepad ${d.pad + 1}` : `Billentyűzet · ${d.layout.toUpperCase()}`;
}
